<script setup>
import { computed } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { useWgptAccount } from 'dashboard/composables/wgpt/useAccount';
import { format } from 'date-fns';

import ButtonV4 from 'next/button/Button.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BillingCard from './components/BillingCard.vue';
import BillingHeader from './components/BillingHeader.vue';
import DetailItem from './components/DetailItem.vue';
import ListingCredits from './components/ListingCredits.vue';

const { currentAccount } = useAccount();
const { isHubpostCarsAccount } = useWgptAccount();

const uiFlags = useMapGetter('accounts/getUIFlags');
const store = useStore();

const wgptAttributes = computed(() => {
  return currentAccount.value.wgpt_attributes || {};
});

/**
 * Computed property for plan name
 * @returns {string|undefined}
 */
const planName = computed(() => {
  return wgptAttributes.value.plan_name;
});

/**
 * Computed property for subscribed quantity
 * @returns {number|undefined}
 */
const subscribedQuantity = computed(() => {
  return wgptAttributes.value.subscribed_quantity;
});

const subscriptionRenewsOn = computed(() => {
  if (!wgptAttributes.value.subscription_ends_on) return '';
  const endDate = new Date(wgptAttributes.value.subscription_ends_on);
  // return date as 12 Jan, 2034
  return format(endDate, 'dd MMM, yyyy');
});

/**
 * Computed property indicating if user has a billing plan
 * @returns {boolean}
 */
const hasABillingPlan = computed(() => {
  return !!planName.value;
});

const onClickBillingPortal = () => {
  store.dispatch('accounts/checkout');
};

const onToggleChatWindow = () => {
  if (window.$whatsgpt) {
    window.$whatsgpt.toggle();
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetchingItem"
    :loading-message="$t('ATTRIBUTES_MGMT.LOADING')"
    :no-records-found="!hasABillingPlan && !isHubpostCarsAccount"
    :no-records-message="$t('WGPT_BILLING.NO_BILLING_USER')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('WGPT_BILLING.TITLE')"
        :description="$t('WGPT_BILLING.DESCRIPTION')"
        :link-text="$t('WGPT_BILLING.VIEW_PRICING')"
        feature-name="billing"
      />
    </template>
    <template #body>
      <ListingCredits v-if="isHubpostCarsAccount" />
      <section v-else class="grid gap-4">
        <BillingCard
          :title="$t('WGPT_BILLING.MANAGE_SUBSCRIPTION.TITLE')"
          :description="$t('WGPT_BILLING.MANAGE_SUBSCRIPTION.DESCRIPTION')"
        >
          <template #action>
            <ButtonV4 sm solid blue @click="onClickBillingPortal">
              {{ $t('WGPT_BILLING.MANAGE_SUBSCRIPTION.BUTTON_TXT') }}
            </ButtonV4>
          </template>
          <div
            v-if="planName || subscribedQuantity || subscriptionRenewsOn"
            class="grid lg:grid-cols-4 sm:grid-cols-3 grid-cols-1 gap-2 divide-x divide-n-weak"
          >
            <DetailItem
              :label="$t('WGPT_BILLING.CURRENT_PLAN.TITLE')"
              :value="planName"
            />
            <DetailItem
              v-if="subscribedQuantity"
              :label="$t('WGPT_BILLING.CURRENT_PLAN.SEAT_COUNT')"
              :value="subscribedQuantity"
            />
            <DetailItem
              v-if="subscriptionRenewsOn"
              :label="$t('WGPT_BILLING.CURRENT_PLAN.RENEWS_ON')"
              :value="subscriptionRenewsOn"
            />
          </div>
        </BillingCard>

        <BillingHeader
          class="px-1 mt-5"
          :title="$t('WGPT_BILLING.CHAT_WITH_US.TITLE')"
          :description="$t('WGPT_BILLING.CHAT_WITH_US.DESCRIPTION')"
        >
          <ButtonV4
            sm
            solid
            slate
            icon="i-lucide-life-buoy"
            @click="onToggleChatWindow"
          >
            {{ $t('WGPT_BILLING.CHAT_WITH_US.BUTTON_TXT') }}
          </ButtonV4>
        </BillingHeader>
      </section>
    </template>
  </SettingsLayout>
</template>
